/**
 * Centralized Constants Configuration
 *
 * This file contains all magic numbers and hardcoded values used throughout the application.
 * Each constant is documented with its purpose and usage context.
 *
 * @module constants
 * @version 4.0
 * @date 2025-11-19
 * @related CQ-003 Code Quality Improvement
 */

// ============================================================================
// MATCH SCORE THRESHOLDS
// Used for subsidy-profile matching quality assessment
// ============================================================================

export const MATCH_THRESHOLDS = {
  /** Minimum score to consider a match valid (0-100 scale) */
  MINIMUM_MATCH_SCORE: 70,

  /** High quality match threshold */
  GOOD_MATCH_SCORE: 80,

  /** Excellent match threshold */
  EXCELLENT_MATCH_SCORE: 90,

  /** Alternative lower threshold for detection confidence */
  LOW_CONFIDENCE_THRESHOLD: 60,

  /** Consultant matching minimum score */
  CONSULTANT_MINIMUM_MATCH: 70,

  // =========================================================================
  // DATABASE-LEVEL FILTERING THRESHOLDS
  // Used for Phase 1 (SQL) filtering to reduce candidates before scoring
  // These are intentionally lower than display thresholds to avoid false negatives
  // =========================================================================

  /** Minimum match_score for pre-computed matches from profile_subsidy_matches table
   * P2-2 FIX: Aligned with backend MIN_QUALITY_SCORE (50) for V5.3.2
   * Quality filtering now properly removes low-scoring matches before storage */
  DB_MATCH_SCORE_MIN: 50,

  /** Higher threshold for prospect profiles (no SIRET/region)
   * Since prospects match more broadly, we require higher quality */
  DB_MATCH_SCORE_MIN_PROSPECT: 70,

  /** Minimum AI relevance score to keep (subsidies below this are rejected)
   * Lowered from 50 to 25 to include profiles with AI scores 28-38 (2026-01-02 fix) */
  AI_RELEVANCE_SCORE_MIN: 25,

  /** Minimum percentage for HybridSubsidyMatcher Phase 2 scoring (V3.0 Funnel Matcher threshold) */
  HYBRID_MATCHER_MIN_PERCENTAGE: 50,

  /** Minimum confidence for TaxonomyMatcher Phase 1 filtering (0-1 scale) */
  TAXONOMY_MIN_CONFIDENCE: 0.30,
} as const;

// ============================================================================
// CONFIDENCE LEVELS (0.0 - 1.0 scale)
// Used for AI/ML predictions and pattern matching
// ============================================================================

export const CONFIDENCE_LEVELS = {
  /** Very high confidence - almost certain (95%) */
  VERY_HIGH: 0.95,

  /** High confidence - very likely correct (90%) */
  HIGH: 0.90,

  /** Good confidence - likely correct (85%) */
  GOOD: 0.85,

  /** Medium-high confidence (80%) */
  MEDIUM_HIGH: 0.80,

  /** Medium confidence - moderately likely (75%) */
  MEDIUM: 0.75,

  /** Lower confidence - somewhat likely (70%) */
  LOW: 0.70,

  /** Baseline confidence - minimum acceptable (50%) */
  BASELINE: 0.50,

  /** Column mapping high confidence threshold */
  MAPPING_HIGH: 0.8,

  /** Column mapping medium confidence threshold */
  MAPPING_MEDIUM: 0.6,
} as const;

// ============================================================================
// SCORING WEIGHTS (0.0 - 1.0 scale)
// ============================================================================
/**
 * @deprecated LEGACY V2 WEIGHTS - DO NOT USE FOR NEW MATCHING
 *
 * KNOWN BUG: "Tennis Neslois" error
 * - ENTITY_TYPE weight (0.08) is too low to block associations from PME grants
 * - A perfect sector match (+0.28) easily overcomes entity mismatch (-0.08)
 * - Result: Associations incorrectly matched to "PME uniquement" grants
 *
 * USE FUNNEL_MATCHER.GATES instead for hard binary eligibility checks.
 * Legal form, geography, and maturity mismatches should be BLOCKERS, not weights.
 */
export const SCORING_WEIGHTS = {
  /** Main scoring component weights */
  SECTOR: 0.28,
  GEOGRAPHIC: 0.18,
  NAF_CODE: 0.14,
  ACTIVITIES: 0.12,
  AMOUNT: 0.10,
  DEADLINE: 0.08,
  ENTITY_TYPE: 0.08,  // ⚠️ TOO LOW - causes Association/PME leakage
  BASE: 0.02,

  /** NAF code match precision weights */
  NAF_3_DIGIT_MATCH: 0.7,  // More precise (5 digits match)
  NAF_2_DIGIT_MATCH: 0.3,  // Less precise (2-3 digits match)

  /** Geographic match precision */
  NATIONAL_NEUTRAL: 0.5,   // National programs (neutral)
  DEPARTEMENT_MATCH: 0.7,  // Department-level match

  /** Activity match weight */
  ACTIVITY_MATCH: 0.8,
} as const;

export const RANKING_WEIGHTS = {
  /** Combined subsidy ranking weights */
  COMBINED_SCORE: 0.40,    // Main relevance score (40%)
  URGENCY: 0.25,           // Deadline urgency (25%)
  AMOUNT_SCORE: 0.20,      // Funding amount (20%)
  AUDIENCE_MATCH: 0.10,    // Target audience fit (10%)
  SECTOR_OVERLAP: 0.05,    // Sector overlap (5%)
} as const;

// ============================================================================
// FUNNEL MATCHER V3.1 CONFIGURATION
// Centralized scoring weights for the 3-stage funnel matcher
// Updated to fix "Tennis Neslois" and "1981" errors
// ============================================================================

export const FUNNEL_MATCHER = {
  /**
   * Stage 1: Hard Gates (Binary pass/fail - NO POINTS, just rejection)
   *
   * These are BLOCKERS, not weights. A mismatch = score of 0.
   * This fixes the "8% trap" where weighted entity type allowed associations
   * to match PME-only grants via high sector scores.
   */
  GATES: {
    /** If true, legal form mismatch (e.g., Association vs PME) = immediate 0 score */
    STRICT_LEGAL_FORM: true,
    /** If true, enforce region+department hierarchy (donut hole fix) */
    STRICT_GEO_HIERARCHY: true,
    /** If true, enforce lifecycle compatibility (1981 fix) */
    STRICT_LIFECYCLE: true,
    /** If true, enforce innovation/geographic regime mutual exclusivity */
    STRICT_FISCAL_REGIME: true,
  },

  /**
   * Penalty Multipliers (applied to FINAL score after alignment)
   *
   * When a gate fails but we want to show "why not eligible" instead of hiding,
   * apply these multipliers to generate a near-zero score with explanation.
   */
  PENALTY_MULTIPLIERS: {
    /** Entity type mismatch (Association vs PME) - kills match completely */
    WRONG_ENTITY_TYPE: 0.0,
    /** Lifecycle mismatch (44-year company vs Creation grant) */
    WRONG_LIFECYCLE: 0.0,
    /** Geographic donut hole (IDF region but Paris excluded) */
    GEO_DONUT_HOLE: 0.0,
    /** Fiscal regime conflict (Innovation regime vs Geographic-only grant) */
    FISCAL_REGIME_CONFLICT: 0.0,
  },

  /**
   * Stage 2: Alignment scoring weights (total 70 points max)
   */
  ALIGNMENT: {
    SECTOR_MAX: 30,           // NAF/sector match points
    SECTOR_EXACT: 30,         // Exact NAF code match
    SECTOR_PREFIX: 25,        // NAF prefix match
    SECTOR_KEYWORD: 15,       // Keyword-based match
    SECTOR_MISMATCH_MULTIPLIER: 0.1, // Score multiplier when sector doesn't match

    THEMATIC_MAX: 20,         // Category/keyword alignment
    THEMATIC_FULL: 15,        // Full category match
    THEMATIC_PARTIAL: 5,      // Partial match

    BUDGET_MAX: 20,           // Budget range alignment
    BUDGET_PERFECT: 20,       // 80%+ overlap
    BUDGET_GOOD: 15,          // 50-80% overlap
    BUDGET_PARTIAL: 10,       // Some overlap
  },

  /**
   * Stage 3: Boost scoring (total 20 points max)
   * DEADLINE SCORING: Longer deadlines = better (more prep time)
   */
  BOOST: {
    DEADLINE: {
      EXPIRED: -20,           // Deadline passed
      CRITICAL: -10,          // < 7 days (almost impossible)
      URGENT: -5,             // 7-14 days (very risky)
      MODERATE: 0,            // 14-30 days (tight but possible)
      OPTIMAL: 10,            // 30-90 days (best prep time)
      COMFORTABLE: 7,         // 90-180 days (plenty of time)
      DISTANT: 3,             // > 180 days (may forget)
      PERMANENT: 5,           // No deadline
    },

    RECURRENCE: {
      PERMANENT: 5,           // Always available
      ANNUAL: 3,              // Yearly cycle
      QUARTERLY: 2,           // Quarterly cycle
      ONE_TIME: 0,            // Single occurrence
    },

    RATE: {
      HIGH: 5,                // >= 70% funding rate
      MEDIUM: 3,              // >= 50% funding rate
      LOW: 0,                 // < 50% funding rate
      HIGH_THRESHOLD: 70,
      MEDIUM_THRESHOLD: 50,
    },
  },

  /**
   * Age limits for special statuses
   */
  AGE_LIMITS: {
    /** Max age for JEI/JEC/JEIR/JEU (French innovation statuses) */
    INNOVATION_STATUS_MAX_YEARS: 8,
    /** Max age for creation grants */
    CREATION_GRANT_MAX_YEARS: 3,
    /** Min age for established business programs */
    ESTABLISHED_MIN_YEARS: 3,
  },

  /**
   * Deadline time windows (days)
   */
  DEADLINE_WINDOWS: {
    CRITICAL: 7,
    URGENT: 14,
    MODERATE: 30,
    OPTIMAL: 90,
    COMFORTABLE: 180,
  },
} as const;

// ============================================================================
// CONSULTANT MATCHING POINTS
// Points awarded for consultant-client matching criteria
// ============================================================================

export const CONSULTANT_MATCHING_POINTS = {
  /** Bonus for being a platform user */
  PLATFORM_USER_BONUS: 20,

  /** Points for specialization match */
  SPECIALIZATION_MATCH: 40,

  /** Points for geographic proximity */
  GEOGRAPHIC_PROXIMITY: 25,

  /** Points for platform status/verification */
  PLATFORM_STATUS: 15,

  /** Points for reputation score */
  REPUTATION: 12,

  /** Points for expertise depth */
  EXPERTISE_DEPTH: 8,
} as const;

// ============================================================================
// TIMING & DELAYS (milliseconds)
// All timing-related constants for debouncing, polling, caching
// ============================================================================

export const TIMING = {
  /** UI debounce delays */
  DEBOUNCE: {
    /** Search input debounce delay */
    SEARCH_INPUT_MS: 300,

    /** General UI input debounce */
    DEFAULT_INPUT_MS: 300,
  },

  /** Polling intervals */
  POLLING: {
    /** Status polling interval (e.g., document upload) */
    STATUS_CHECK_MS: 2000,

    /** Dashboard refresh interval */
    DASHBOARD_REFRESH_MS: 2000,

    /** Health check interval (5 minutes) */
    HEALTH_CHECK_MS: 5 * 60 * 1000,
  },

  /** Cache durations */
  CACHE: {
    /** React Query stale time (5 minutes) */
    STALE_TIME_MS: 5 * 60 * 1000,

    /** React Query garbage collection time (10 minutes) */
    GC_TIME_MS: 10 * 60 * 1000,

    /** Subsidy data consistency cache (10 minutes) */
    SUBSIDY_DATA_MS: 10 * 60 * 1000,

    /** Recent metrics window (5 minutes) */
    RECENT_METRICS_MS: 5 * 60 * 1000,

    /** Circuit breaker reset timeout (5 minutes) */
    CIRCUIT_BREAKER_RESET_MS: 5 * 60 * 1000,

    /** Sidebar cookie max age (7 days) */
    SIDEBAR_COOKIE_MS: 7 * 24 * 60 * 60 * 1000,
  },

  /** Timeout durations */
  TIMEOUT: {
    /** Feedback submission timeout */
    FEEDBACK_MS: 2000,

    /** Application submission timeout */
    APPLICATION_SUBMIT_MS: 3000,

    /** Bulk import delay between batches */
    BULK_IMPORT_MS: 1000,

    /** Enrichment service request delay */
    ENRICHMENT_REQUEST_MS: 1200,
  },

  /** Animation durations */
  ANIMATION: {
    /** Standard transition duration */
    TRANSITION_MS: 300,

    /** Progress chart transition */
    PROGRESS_CHART_MS: 700,

    /** Animated sequence steps */
    SEQUENCE: [0, 1000, 1800, 2500] as const,
  },

  /** Toast/notification durations */
  NOTIFICATION: {
    /** Standard toast duration */
    STANDARD_MS: 2000,

    /** Short notification */
    SHORT_MS: 1500,

    /** Long notification */
    LONG_MS: 4000,
  },

  /** Rate limiting delays */
  RATE_LIMIT: {
    /** INSEE API rate limit delay */
    INSEE_API_MS: 300,

    /** Additional safety delay */
    ADDITIONAL_MS: 500,

    /** Batch processing delay */
    BATCH_PROCESSING_MS: 100,

    /** Database optimization delay */
    DB_OPTIMIZATION_MS: 50,
  },
} as const;

// ============================================================================
// FILE UPLOAD LIMITS (bytes)
// Maximum file sizes for different upload contexts
// ============================================================================

export const FILE_UPLOAD = {
  /** Maximum file size for general document uploads (50 MB) */
  MAX_FILE_SIZE_BYTES: 50 * 1024 * 1024,

  /** Maximum file size for logo uploads (2 MB) */
  MAX_LOGO_SIZE_BYTES: 2 * 1024 * 1024,

  /** Maximum file size for profile documents (10 MB) */
  MAX_PROFILE_DOC_SIZE_BYTES: 10 * 1024 * 1024,

  /** Maximum file size for Google Vision API (20 MB) */
  GOOGLE_VISION_LIMIT_BYTES: 20 * 1024 * 1024,

  /** Maximum number of files per upload batch */
  MAX_FILES_PER_UPLOAD: 10,

  /** Test file sizes for validation */
  TEST_SIZES: {
    SMALL_PDF_BYTES: 1024 * 100,        // 100 KB
    MEDIUM_EXCEL_BYTES: 1024 * 1024 * 5, // 5 MB
    LARGE_WORD_BYTES: 1024 * 1024 * 45,  // 45 MB
    MAX_SIZE_BYTES: 50 * 1024 * 1024,    // 50 MB
    OVERSIZED_BYTES: 55 * 1024 * 1024,   // 55 MB (should fail)
  },
} as const;

// ============================================================================
// PAGINATION & LIMITS
// Array sizes, query limits, and pagination settings
// ============================================================================

export const LIMITS = {
  /** Search query constraints */
  SEARCH: {
    /** Maximum search query length */
    MAX_QUERY_LENGTH: 500,

    /** Maximum keyword length */
    MAX_KEYWORD_LENGTH: 100,

    /** Maximum number of keywords */
    MAX_KEYWORDS: 50,

    /** Maximum results to return */
    MAX_RESULTS: 10000,
  },

  /** Array size limits */
  ARRAY: {
    /** Maximum generic array items */
    MAX_ITEMS: 100,

    /** Maximum event array size (analytics) */
    MAX_EVENTS: 100,

    /** Maximum error tracking entries */
    MAX_ERRORS: 100,

    /** Maximum monitoring errors stored */
    MAX_MONITORING_ERRORS: 100,

    /** Maximum cache entries */
    MAX_CACHE_ENTRIES: 1000,
  },

  /** Database query limits */
  DATABASE: {
    /** Page size for data streaming */
    STREAM_PAGE_SIZE: 1000,

    /** Preload batch size */
    PRELOAD_BATCH_SIZE: 50,

    /** Single record limit */
    SINGLE_RECORD: 1,

    /** Small batch size */
    SMALL_BATCH_SIZE: 10,
  },

  /** Text length constraints */
  TEXT: {
    /** Display truncation length */
    DISPLAY_TRUNCATION: 100,

    /** Minimum text length for filters */
    MIN_FILTER_LENGTH: 5,

    /** Maximum criteria/restriction length */
    MAX_CRITERIA_LENGTH: 200,

    /** Minimum item text length (avoid false positives) */
    MIN_ITEM_LENGTH: 20,

    /** Paragraph split threshold */
    PARAGRAPH_SPLIT_LENGTH: 150,

    /** Minimum finding length threshold */
    MIN_FINDING_LENGTH: 20,

    /** Minimum criterion validation length */
    MIN_CRITERION_LENGTH: 5,
  },
} as const;

// ============================================================================
// PERFORMANCE THRESHOLDS
// Response times, memory usage, and performance budgets
// ============================================================================

export const PERFORMANCE = {
  /** Response time thresholds (milliseconds) */
  RESPONSE_TIME: {
    /** Database query degraded threshold */
    DATABASE_DEGRADED_MS: 2000,

    /** API call degraded threshold */
    API_DEGRADED_MS: 3000,

    /** External service degraded threshold */
    EXTERNAL_DEGRADED_MS: 5000,

    /** Average API response time threshold */
    AVERAGE_API_MS: 1000,

    /** Scaling up threshold (horizontal scaling) */
    SCALE_UP_MS: 2000,

    /** Scaling down threshold (horizontal scaling) */
    SCALE_DOWN_MS: 500,
  },

  /** Memory usage thresholds (percentage) */
  MEMORY: {
    /** Memory usage unhealthy threshold */
    UNHEALTHY_PERCENT: 90,

    /** Memory usage degraded threshold */
    DEGRADED_PERCENT: 70,
  },

  /** Web Vitals thresholds */
  WEB_VITALS: {
    /** Largest Contentful Paint (LCP) threshold (ms) */
    LCP_MS: 2500,

    /** First Input Delay (FID) threshold (ms) */
    FID_MS: 100,
  },

  /** Storage limits */
  STORAGE: {
    /** Maximum metrics stored in memory */
    MAX_METRICS: 1000,

    /** Maximum API call records stored */
    MAX_API_CALLS: 500,
  },

  /** Compression thresholds */
  COMPRESSION: {
    /** Size threshold for compression level 6 vs 9 (bytes) */
    SIZE_THRESHOLD_BYTES: 100000,
  },

  /** Scaling thresholds */
  SCALING: {
    /** Queue depth threshold for scaling up */
    QUEUE_SCALE_UP: 100,

    /** Queue depth threshold for scaling down */
    QUEUE_SCALE_DOWN: 10,
  },

  /** Baseline metrics */
  BASELINE: {
    /** Normal bounce rate percentage */
    BOUNCE_RATE_PERCENT: 15,

    /** Normal error rate percentage */
    ERROR_RATE_PERCENT: 2,
  },

  /** Trend analysis */
  ANALYSIS: {
    /** Minimum history points required for trend analysis */
    MIN_HISTORY_POINTS: 10,
  },
} as const;

// ============================================================================
// DASHBOARD HIGHLIGHT THRESHOLDS (Display Priority)
// Used for UI badges/highlights - NOT for match quality scoring
//
// IMPORTANT: This is separate from FUNNEL_MATCHER.BOOST.DEADLINE which
// determines MATCH QUALITY (urgent = harder to apply = lower quality).
// Here, urgency = higher DISPLAY PRIORITY (show it prominently).
// ============================================================================

export const DASHBOARD_HIGHLIGHT = {
  /** Very urgent - less than 7 days → Red badge, top of list */
  URGENT_DAYS: 7,
  URGENT_PRIORITY: 1.0,

  /** Soon - less than 30 days → Orange badge */
  SOON_DAYS: 30,
  SOON_PRIORITY: 0.7,

  /** Normal - less than 90 days → No special badge */
  NORMAL_DAYS: 90,
  NORMAL_PRIORITY: 0.4,

  /** Rolling/no deadline → Grey "permanent" badge */
  NO_DEADLINE_PRIORITY: 0.3,
} as const;

/**
 * @deprecated Use DASHBOARD_HIGHLIGHT instead
 * Kept for backward compatibility with existing code
 */
export const DEADLINE_URGENCY = {
  URGENT_DAYS: 7,
  URGENT_SCORE: 1.0,
  SOON_DAYS: 30,
  SOON_SCORE: 0.7,
  NORMAL_DAYS: 90,
  NORMAL_SCORE: 0.4,
  NO_DEADLINE_SCORE: 0.3,
} as const;

// ============================================================================
// AI TRIAL LIMITS
// Free tier usage limits for AI-powered features
// ============================================================================

export const AI_TRIAL = {
  /** Maximum free AI feature uses */
  MAX_FREE_USES: 3,

  /** Display value during testing mode */
  TESTING_MODE_DISPLAY: 999,
} as const;

// ============================================================================
// TIER & PRICING LIMITS
// Subscription tier profile limits
// ============================================================================

export const TIER_LIMITS = {
  /** Maximum profiles per tier */
  DISCOVER_PROFILES: 1,
  PRO_20_PROFILES: 20,
  PRO_40_PROFILES: 40,
  PRO_100_PROFILES: 100,

  /** Pricing in cents (for Stripe) */
  PRICING_CENTS: {
    DISCOVER_MONTHLY: 400,      // €4.00
    SINGLE_MONTHLY: 599,        // €5.99
    SINGLE_YEARLY: 5900,        // €59.00/year
    PRO_20_MONTHLY: 1499,       // €14.99
    PRO_20_YEARLY: 14900,       // €149.00/year
    PRO_40_MONTHLY: 1999,       // €19.99
    PRO_40_YEARLY: 19900,       // €199.00/year
    PRO_100_MONTHLY: 4999,      // €49.99
    PRO_100_YEARLY: 49900,      // €499.00/year
  },
} as const;

// ============================================================================
// QUALITY THRESHOLDS (display strings and actual values)
// Used for quality assessment displays and conditional rendering
// ============================================================================

export const QUALITY_THRESHOLDS = {
  /** Match quality ranges for display */
  EXCELLENT: {
    MIN_PERCENT: 90,
    DISPLAY: '90%+',
    LABEL: 'Excellent',
  },
  HIGH: {
    MIN_PERCENT: 85,
    MAX_PERCENT: 90,
    DISPLAY: '85-90%',
    LABEL: 'High Quality',
  },
  GOOD: {
    MIN_PERCENT: 70,
    MAX_PERCENT: 80,
    DISPLAY: '70-80%',
    LABEL: 'Good',
  },
  MODERATE: {
    MIN_PERCENT: 60,
    MAX_PERCENT: 70,
    DISPLAY: '60-70%',
    LABEL: 'Moderate',
  },

  /** Target accuracy for systems */
  TARGET_ACCURACY_PERCENT: 85,
} as const;

// ============================================================================
// COLUMN MAPPING CONFIDENCE
// Confidence levels for automated column mapping during imports
// ============================================================================

export const COLUMN_MAPPING_CONFIDENCE = {
  /** Very high confidence mapping */
  VERY_HIGH: 0.9,

  /** High confidence mapping */
  HIGH: 0.7,

  /** Moderate confidence mapping */
  MODERATE: 0.5,

  /** Low confidence mapping */
  LOW: 0.3,
} as const;

// ============================================================================
// HELPER FUNCTIONS
// Utility functions for working with constants
// ============================================================================

/**
 * Get match quality label based on score (0-100 scale)
 */
export function getMatchQualityLabel(score: number): string {
  if (score >= QUALITY_THRESHOLDS.EXCELLENT.MIN_PERCENT) {
    return QUALITY_THRESHOLDS.EXCELLENT.LABEL;
  }
  if (score >= QUALITY_THRESHOLDS.HIGH.MIN_PERCENT) {
    return QUALITY_THRESHOLDS.HIGH.LABEL;
  }
  if (score >= QUALITY_THRESHOLDS.GOOD.MIN_PERCENT) {
    return QUALITY_THRESHOLDS.GOOD.LABEL;
  }
  return QUALITY_THRESHOLDS.MODERATE.LABEL;
}

/**
 * Check if a match score is acceptable
 */
export function isAcceptableMatch(score: number): boolean {
  return score >= MATCH_THRESHOLDS.MINIMUM_MATCH_SCORE;
}

/**
 * Check if confidence level meets threshold
 */
export function meetsConfidenceThreshold(
  confidence: number,
  threshold: keyof typeof CONFIDENCE_LEVELS
): boolean {
  return confidence >= CONFIDENCE_LEVELS[threshold];
}

/**
 * Get urgency score based on days until deadline
 */
export function getDeadlineUrgencyScore(daysUntilDeadline: number | null): number {
  if (daysUntilDeadline === null) {
    return DEADLINE_URGENCY.NO_DEADLINE_SCORE;
  }
  if (daysUntilDeadline <= DEADLINE_URGENCY.URGENT_DAYS) {
    return DEADLINE_URGENCY.URGENT_SCORE;
  }
  if (daysUntilDeadline <= DEADLINE_URGENCY.SOON_DAYS) {
    return DEADLINE_URGENCY.SOON_SCORE;
  }
  if (daysUntilDeadline <= DEADLINE_URGENCY.NORMAL_DAYS) {
    return DEADLINE_URGENCY.NORMAL_SCORE;
  }
  return DEADLINE_URGENCY.NO_DEADLINE_SCORE;
}

/**
 * Convert bytes to human-readable format
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

// ============================================================================
// TYPE EXPORTS
// Export types for TypeScript consumers
// ============================================================================

export type MatchThreshold = keyof typeof MATCH_THRESHOLDS;
export type ConfidenceLevel = keyof typeof CONFIDENCE_LEVELS;
export type ScoringWeight = keyof typeof SCORING_WEIGHTS;
export type TimingCategory = keyof typeof TIMING;
export type LimitCategory = keyof typeof LIMITS;
export type PerformanceMetric = keyof typeof PERFORMANCE;
